import { isEmpty } from "lodash";
import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import Button from "antd/lib/button";
import Tooltip from "@/components/Tooltip";
import routeWithUserSession from "@/components/ApplicationArea/routeWithUserSession";
import BigMessage from "@/components/BigMessage";
import PageHeader from "@/components/PageHeader";
import Parameters from "@/components/Parameters";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Filters from "@/components/Filters";

import { Dashboard } from "@/services/dashboard";
import routes from "@/services/routes";
import { policy } from "@/services/policy";
import { clientConfig } from "@/services/auth";
import { durationHumanize } from "@/lib/utils";

import { ThemeProvider, useTheme } from "@/components/ThemeProvider";
import useDashboard from "./hooks/useDashboard";

import "./SharedDashboardPage.less";
import "@/styles/dark-theme.less";

function DashboardBackgroundLayer({ dashboard }) {
  const bg = dashboard && dashboard.options && dashboard.options.background;
  if (!bg || !bg.image_url) return null;
  const style = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 0,
    pointerEvents: "none",
    backgroundSize: "100% 100%",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: `url(${bg.image_url})`,
    opacity: bg.opacity !== undefined ? bg.opacity : 1.0,
  };
  return <div className="dashboard-bg" style={style} />;
}

/**
 * Read URL parameters for component visibility control.
 * Supports: hide_header, hide_timestamp, hide_parameters, hide_filters
 */
function getVisibilityParams() {
  const params = new URLSearchParams(window.location.search); // eslint-disable-line compat/compat
  return {
    hideHeader: params.get("hide_header") === "true",
    hideTimestamp: params.get("hide_timestamp") === "true",
    hideParameters: params.get("hide_parameters") === "true",
    hideFilters: params.get("hide_filters") === "true",
  };
}

function ThemeToggleButton() {
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <Tooltip title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}>
      <Button
        className="theme-toggle-btn m-l-5"
        onClick={toggleTheme}
        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      >
        <i
          className={cx("zmdi", {
            "zmdi-brightness-7": theme === "dark",   // sun icon for dark mode (click to go light)
            "zmdi-brightness-2": theme !== "dark",   // moon icon for light mode (click to go dark)
          })}
          aria-hidden="true"
        />
      </Button>
    </Tooltip>
  );
}

function SharedDashboardHeader({ dashboardConfiguration }) {
  const {
    dashboard,
    refreshRate,
    setRefreshRate,
    disableRefreshRate,
    refreshing,
    refreshDashboard,
  } = dashboardConfiguration;

  const allowedIntervals = policy.getDashboardRefreshIntervals();
  const refreshRateOptions = clientConfig.dashboardRefreshIntervals;
  const onRefreshRateSelected = ({ key }) => {
    const parsedRefreshRate = parseFloat(key);
    if (parsedRefreshRate) {
      setRefreshRate(parsedRefreshRate);
      refreshDashboard();
    } else {
      disableRefreshRate();
    }
  };

  return (
    <div className="dashboard-header">
      <PageHeader title={dashboard.name} />
      <div className="dashboard-control">
        <span className="hidden-print">
          <Button.Group>
            <Tooltip title={refreshRate ? `Auto Refreshing every ${durationHumanize(refreshRate)}` : null}>
              <Button type={refreshRate ? "primary" : "default"} onClick={() => refreshDashboard()}>
                <i className={cx("zmdi zmdi-refresh m-r-5", { "zmdi-hc-spin": refreshing })} aria-hidden="true" />
                {refreshRate ? durationHumanize(refreshRate) : "Refresh"}
              </Button>
            </Tooltip>
            <ThemeToggleButton />
          </Button.Group>
        </span>
      </div>
    </div>
  );
}

SharedDashboardHeader.propTypes = {
  dashboardConfiguration: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

function SharedDashboard({ dashboard }) {
  const dashboardConfiguration = useDashboard(dashboard);
  const {
    filters,
    setFilters,
    globalParameters,
    refreshDashboard,
    loadWidget,
    refreshWidget,
  } = dashboardConfiguration;

  // Read visibility settings from URL params
  const visibility = getVisibilityParams();

  // Also check dashboard options for visibility settings (DB-persisted settings)
  const options = dashboard.options || {};
  const hideHeader = visibility.hideHeader || options.hide_header === true;
  const hideTimestamp = visibility.hideTimestamp || options.hide_timestamp === true;
  const hideParameters = visibility.hideParameters || options.hide_parameters === true;
  const hideFilters = visibility.hideFilters || options.hide_filters === true;

  // Apply hide-timestamps class to body for CSS-based hiding of widget footers
  React.useEffect(() => {
    if (hideTimestamp) {
      document.body.classList.add("hide-timestamps");
    }
    return () => {
      document.body.classList.remove("hide-timestamps");
    };
  }, [hideTimestamp]);

  return (
    <div className="container p-t-10 p-b-20">
      {!hideHeader && <SharedDashboardHeader dashboardConfiguration={dashboardConfiguration} />}
      {!hideParameters && !isEmpty(globalParameters) && (
        <div className="dashboard-parameters m-b-10 p-15 bg-white tiled" data-test="DashboardParameters">
          <Parameters parameters={globalParameters} onValuesChange={refreshDashboard} />
        </div>
      )}
      {!hideFilters && !isEmpty(filters) && (
        <div className="m-b-10 p-15 bg-white tiled" data-test="DashboardFilters">
          <Filters filters={filters} onChange={setFilters} />
        </div>
      )}
      <div id="dashboard-container" style={{ position: "relative" }}>
        <DashboardBackgroundLayer dashboard={dashboardConfiguration.dashboard} />
        <DashboardGrid
          dashboard={dashboardConfiguration.dashboard}
          widgets={dashboardConfiguration.dashboard.widgets}
          filters={filters}
          isEditing={false}
          isPublic
          onLoadWidget={loadWidget}
          onRefreshWidget={refreshWidget}
        />
      </div>
    </div>
  );
}

SharedDashboard.propTypes = {
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

class SharedDashboardPage extends React.Component {
  static propTypes = {
    token: PropTypes.string.isRequired,
    onError: PropTypes.func,
  };

  static defaultProps = {
    onError: () => {},
  };

  state = {
    loading: true,
    dashboard: null,
  };

  componentDidMount() {
    // 确保 headless 模式生效（隐藏导航栏）
    document.body.classList.add("headless");
    
    Dashboard.getSharedByToken({ token: this.props.token })
      .then(dashboard => this.setState({ dashboard, loading: false }))
      .catch(error => this.props.onError(error));
  }

  componentWillUnmount() {
    document.body.classList.remove("headless");
  }

  render() {
    const { loading, dashboard } = this.state;
    return (
      <ThemeProvider>
        <div className="shared-dashboard-page">
          {loading ? (
            <div className="container loading-message">
              <BigMessage className="" icon="fa-spinner fa-2x fa-pulse" message="Loading..." />
            </div>
          ) : (
            <SharedDashboard dashboard={dashboard} />
          )}
        </div>
      </ThemeProvider>
    );
  }
}

routes.register(
  "Dashboards.SharedView",
  routeWithUserSession({
    path: "/shared/dashboards/:token",
    render: pageProps => <SharedDashboardPage {...pageProps} />,
    bodyClass: "headless",
  })
);
